import React, { useEffect, useState, useCallback } from "react";
import { Icon } from "@iconify/react";
import api from "../services/api";
import PageHeader from "../components/common/PageHeader";
import { showToast } from "../helper/notifications";

const INITIAL_FORM = {
  id: null,
  company_name: "",
  short_name: "",
  legal_form: "",
  store_code: "",
  description: "",
  street_address: "",
  house_number: "",
  postal_code: "",
  city: "",
  state: "",
  country: "",
  lat: "",
  lng: "",
  phone_number: "",
  email: "",
  website_url: "",
  vat_id: "",
  registration_number: "",
  google_link: "",
  google_account_id: "",
  google_place_id: "",
  linkedin_link: "",
  instagram_link: "",
  facebook_link: "",
  youtube_link: "",
};

const CompanyPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [originalData, setOriginalData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCompany = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/company");
      if (res.data) {
        const mapped = {
          id: res.data.id ?? null,
          company_name: res.data.company_name ?? "",
          short_name: res.data.short_name ?? "",
          legal_form: res.data.legal_form ?? "",
          store_code: res.data.store_code ?? "",
          description: res.data.description ?? "",
          street_address: res.data.street_address ?? "",
          house_number: res.data.house_number ?? "",
          postal_code: res.data.postal_code ?? "",
          city: res.data.city ?? "",
          state: res.data.state ?? "",
          country: res.data.country ?? "",
          lat: res.data.lat ?? "",
          lng: res.data.lng ?? "",
          phone_number: res.data.phone_number ?? "",
          email: res.data.email ?? "",
          website_url: res.data.website_url ?? "",
          vat_id: res.data.vat_id ?? "",
          registration_number: res.data.registration_number ?? "",
          google_link: res.data.google_link ?? "",
          google_account_id: res.data.google_account_id ?? "",
          google_place_id: res.data.google_place_id ?? "",
          linkedin_link: res.data.linkedin_link ?? "",
          instagram_link: res.data.instagram_link ?? "",
          facebook_link: res.data.facebook_link ?? "",
          youtube_link: res.data.youtube_link ?? "",
        };
        setFormData(mapped);
        setOriginalData(mapped);
      }
    } catch (err) {
      console.error(err);
      showToast("error", "Failed to load company data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData(originalData);
    showToast("info", "Changes reset");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      showToast("error", "Company Name is required");
      return;
    }

    setSaving(true);
    try {
      const res = await api.put("/company", formData);
      if (res.data?.data) {
        const updated = {
          ...formData,
          ...Object.fromEntries(
            Object.entries(res.data.data).map(([k, v]) => [k, v ?? ""])
          ),
        };
        setFormData(updated);
        setOriginalData(updated);
      }
      showToast("success", "Company data saved successfully");
    } catch (err) {
      console.error(err);
      showToast("error", err.response?.data?.message || "Failed to save company data");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Company"
        subtitle="Manage enterprise master data and contact information."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              disabled={loading || saving}
              className="btn btn-outline-secondary radius-8 px-16 py-8 d-flex align-items-center gap-2 text-xs fw-semibold"
            >
              <Icon icon="solar:restart-linear" className="text-base" />
              Reset
            </button>
            <button
              type="submit"
              form="company-data-form"
              disabled={loading || saving}
              className="btn btn-primary-600 radius-8 px-20 py-8 d-flex align-items-center gap-2 text-xs fw-semibold"
            >
              <Icon
                icon={saving ? "line-md:loading-twotone-loop" : "solar:diskette-linear"}
                className="text-base"
              />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="card radius-12 p-40 text-center text-secondary-light text-sm">
          Loading company profile...
        </div>
      ) : (
        <form id="company-data-form" onSubmit={handleSubmit}>
          <div className="row gy-4">
            {/* General Company Information */}
            <div className="col-lg-12">
              <div className="card radius-12">
                <div className="card-header border-bottom bg-base py-14 px-24 d-flex align-items-center gap-2">
                  <Icon icon="solar:buildings-2-linear" className="text-lg" />
                  <h6 className="text-sm fw-semibold mb-0">General Information</h6>
                </div>
                <div className="card-body p-24">
                  <div className="row gy-3">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Company Name <span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. Messe München GmbH"
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Short / Brand Name
                      </label>
                      <input
                        type="text"
                        name="short_name"
                        value={formData.short_name}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. Messe München"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Legal Form
                      </label>
                      <input
                        type="text"
                        name="legal_form"
                        value={formData.legal_form}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. GmbH"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Store / Client Code
                      </label>
                      <input
                        type="text"
                        name="store_code"
                        value={formData.store_code}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. LQ-DE-MM-00001"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        VAT ID (USt-IdNr.)
                      </label>
                      <input
                        type="text"
                        name="vat_id"
                        value={formData.vat_id}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. DE129424628"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Commercial Register (Handelsregister)
                      </label>
                      <input
                        type="text"
                        name="registration_number"
                        value={formData.registration_number}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="e.g. HRB 6311 (Amtsgericht München)"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="form-control radius-8"
                        placeholder="Company description..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Address & Coordinates */}
            <div className="col-lg-6">
              <div className="card radius-12 h-100">
                <div className="card-header border-bottom bg-base py-14 px-24 d-flex align-items-center gap-2">
                  <Icon icon="solar:map-point-linear" className="text-lg" />
                  <h6 className="text-sm fw-semibold mb-0">Headquarters Address</h6>
                </div>
                <div className="card-body p-24">
                  <div className="row gy-3">
                    <div className="col-md-9">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Street Address
                      </label>
                      <input
                        type="text"
                        name="street_address"
                        value={formData.street_address}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="Street"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        House No.
                      </label>
                      <input
                        type="text"
                        name="house_number"
                        value={formData.house_number}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="No."
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="ZIP / Postal Code"
                      />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="City"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        State / Federal State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="State"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="Country"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Latitude
                      </label>
                      <input
                        type="text"
                        name="lat"
                        value={formData.lat}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="48.13551820"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Longitude
                      </label>
                      <input
                        type="text"
                        name="lng"
                        value={formData.lng}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="11.69244600"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact, Google Integration & Social Links */}
            <div className="col-lg-6">
              <div className="card radius-12 h-100">
                <div className="card-header border-bottom bg-base py-14 px-24 d-flex align-items-center gap-2">
                  <Icon icon="solar:phone-calling-linear" className="text-lg" />
                  <h6 className="text-sm fw-semibold mb-0">Contact & Digital Presence</h6>
                </div>
                <div className="card-body p-24">
                  <div className="row gy-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="+49 89 ..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="info@company.de"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Website URL
                      </label>
                      <input
                        type="url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Google Maps Link
                      </label>
                      <input
                        type="url"
                        name="google_link"
                        value={formData.google_link}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://maps.google.com/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Google Account ID
                      </label>
                      <input
                        type="text"
                        name="google_account_id"
                        value={formData.google_account_id}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="accounts/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Google Location ID
                      </label>
                      <input
                        type="text"
                        name="google_place_id"
                        value={formData.google_place_id}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="locations/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        LinkedIn URL
                      </label>
                      <input
                        type="url"
                        name="linkedin_link"
                        value={formData.linkedin_link}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://www.linkedin.com/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Instagram URL
                      </label>
                      <input
                        type="url"
                        name="instagram_link"
                        value={formData.instagram_link}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://www.instagram.com/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        Facebook URL
                      </label>
                      <input
                        type="url"
                        name="facebook_link"
                        value={formData.facebook_link}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://www.facebook.com/..."
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-xs mb-6">
                        YouTube URL
                      </label>
                      <input
                        type="url"
                        name="youtube_link"
                        value={formData.youtube_link}
                        onChange={handleChange}
                        className="form-control radius-8"
                        placeholder="https://www.youtube.com/..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default CompanyPage;
